import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Lottie from 'lottie-react';

import downloadFileBg from '@assets/images/downloadFileBg.svg';
import downloadFileError from '@assets/images/downloadFileError.svg';
import markAnyLfsLogo from '@assets/images/markAnyLfsLogo.svg';
import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import { userCustomerDeliveryFileAuthService } from '@services/public/user/download/customer-delivery-file/file-auth';
import { is200xRange } from '@utils/utilities';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import { Controller, useForm } from 'react-hook-form';
import { userCustomerDeliveryFileDownloadService } from '@services/public/user/download/customer-delivery-file/file-download';
import fileDownloadLottie from '@assets/lottie/fileDownload.json';

type DownloadFileData = {
  downInfo: {
    expirationYmd: string;
    s3FileList: [{ s3DownUrl: string }];
  } | null;
  userConfigEnv: {
    cssJsVersion: string;
  } | null;
};

const FileDownloadLottie = () => (
  <Box>
    <Lottie animationData={fileDownloadLottie} loop={true} />
  </Box>
);

const CustomerDeliveryFileDownload = () => {
  const { FILEPWD } = VALIDATION_MESSAGE.DOWNLOAD.CUSTOMER_DELIVERY;

  const customerDeliveryDownloadFileSchema = yup.object({
    filePwd: yup
      .string()
      .required(FILEPWD.REQUIRED)
      .min(4, FILEPWD.MIN_LENGTH)
      .max(20, FILEPWD.MAX_LENGTH)
      .default(''),
  });

  type CustomerDeliveryDownloadFileFormData = yup.InferType<
    typeof customerDeliveryDownloadFileSchema
  >;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CustomerDeliveryDownloadFileFormData>({
    resolver: yupResolver(customerDeliveryDownloadFileSchema),
  });

  const { fileId } = useParams();
  const [downloadFileData, setDownloadFileData] = useState<DownloadFileData>({
    downInfo: null,
    userConfigEnv: null,
  });
  const [isDownloadable, setIsDownloadable] = useState(true);
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);

  const onSubmit = (data) => {
    const { filePwd } = data;
    fileDownloadWithAuth(fileId, filePwd);
  };

  function startDownload(url) {
    window.location.href = url;
  }

  const fileDownload = async (key) => {
    const { response, statusCode } =
      await userCustomerDeliveryFileDownloadService(key);

    if (response?.code === '1' && is200xRange(statusCode)) {
      setIsPasswordCorrect(true);
      if (response?.result?.downInfo?.s3FileList.length > 0) {
        response?.result?.downInfo?.s3FileList.forEach((file, index) => {
          setTimeout(() => {
            startDownload(file.s3DownUrl);
          }, index * 1000);
        });
      }
      setDownloadFileData({
        downInfo: response?.result?.downInfo,
        userConfigEnv: response?.result?.userConfigEnv,
      });
    } else if (response?.code === '53100') {
      // @ts-ignore
      setDownloadFileData((prevDownloadFileData) => {
        return {
          ...prevDownloadFileData,
          downInfo: {
            ...prevDownloadFileData.downInfo,
            expirationYmd: response?.result?.downInfo?.expirationYmd,
          },
        };
      });
      setIsPasswordCorrect(false);
    } else {
      setIsDownloadable(false);
    }
  };

  const fileDownloadWithAuth = async (key, filePwd) => {
    const {
      response: authResponse,
      statusCode: authStatusCode,
      error: authError,
    } = await userCustomerDeliveryFileAuthService(key, {
      filePwd,
    });

    if (authResponse?.code === '1' && is200xRange(authStatusCode)) {
      setIsPasswordCorrect(true);
      startDownload(authResponse?.result?.downInfo?.s3FileList[0]?.s3DownUrl);
      // @ts-ignore
      setDownloadFileData((prevDownloadFileData) => {
        return {
          ...prevDownloadFileData,
          downInfo: {
            ...prevDownloadFileData.downInfo,
            downInfo: authResponse?.result?.downInfo,
            userConfigEnv: authResponse?.result?.userConfigEnv,
          },
        };
      });
    }

    // @ts-expect-error 53120
    if (authError?.response?.data?.code === '53120') {
      return;
    }

    if (
      authError ||
      !is200xRange(authStatusCode) ||
      (authResponse?.code !== '0' && authResponse?.code !== '1')
    ) {
      setIsDownloadable(false);
    }
  };

  useEffect(() => {
    fileDownload(fileId);
  }, []);

  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="center"
      alignItems="center"
      sx={{
        backgroundImage: `url(${downloadFileBg})`,
        backgroundPosition: 'bottom',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {!isDownloadable && (
        <Box display="flex" flexDirection="column" gap={5}>
          <Box margin="0 auto">
            <img
              src={downloadFileError}
              alt="download file error"
              width={250}
              height={200}
            />
          </Box>
          <Typography
            fontSize="17px"
            lineHeight="26px"
            fontWeight={400}
            color="var(--base-black)"
          >
            요청한 파일은 다운로드할 수 없습니다.
          </Typography>
        </Box>
      )}

      {isDownloadable && !isPasswordCorrect && (
        <Box
          component="form"
          display="flex"
          flexDirection="column"
          gap={10}
          minWidth={360}
          maxWidth={360}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box display="flex" justifyContent="center">
            <img
              src={markAnyLfsLogo}
              alt="MarkAny LFS"
              width={274}
              height={51}
            />
          </Box>
          <Controller
            name="filePwd"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  isPassword
                  label="파일 비밀번호"
                  placeholder="비밀번호를 입력해 주세요."
                  required
                  showErrorMsg={!!errors.filePwd}
                  errorMessage={errors.filePwd && errors.filePwd.message}
                  defaultValue={field.value}
                  value={field.value}
                />
              );
            }}
          />

          <Box display="flex" flexDirection="column" gap="14px">
            <Button variant="contained" type="submit">
              인증
            </Button>
            <Box display="flex" flexDirection="row" gap="10px">
              <Typography
                variant="body1"
                fontWeight={600}
                color="var(--neutral-900)"
              >
                다운로드 만료일:
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                color="var(--base-black)"
              >
                {downloadFileData?.downInfo?.expirationYmd}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {isDownloadable && isPasswordCorrect && (
        <Box
          display="flex"
          flexDirection="column"
          gap={5}
          maxWidth="302px"
          minWidth="302px"
          alignItems="center"
        >
          <FileDownloadLottie />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={3}
          >
            <Typography
              fontSize="17px"
              lineHeight="26px"
              fontWeight={400}
              color="var(--base-black)"
              textAlign="center"
            >
              파일이 다운로드 중입니다. 완료까지 잠시만 기다려주세요.
            </Typography>

            <Box display="flex" flexDirection="row" gap="10px">
              <Typography
                variant="body1"
                fontWeight={600}
                color="var(--neutral-900)"
              >
                다운로드 만료일:
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                color="var(--base-black)"
              >
                {downloadFileData?.downInfo?.expirationYmd}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomerDeliveryFileDownload;
